import React from 'react';
import { ExternalLink, BookOpen, Building, Gavel, FileText, Users, Briefcase, Award, Scale, Tag } from 'lucide-react';
import { products } from '../data/mockData';

const iconMap = {
  Scale,
  BookOpen,
  Building,
  Gavel,
  FileText,
  Users,
  Briefcase,
  Award
};

const ProductsSection = () => {
  return (
    <section id="produtos" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Nossos <span className="text-orange-500">Flashcards</span>
          </h2>
          <p className="text-xl text-gray-600">
            Escolha a disciplina e comece sua jornada rumo à aprovação. 
            Todos com <strong className="text-orange-600">50% de desconto!</strong>
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto mb-16">
          {products.map((product) => {
            const IconComponent = iconMap[product.icon];
            return (
              <div 
                key={product.id}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100"
              >
                {/* Discount Badge */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 text-center font-bold relative">
                  <Tag className="inline-block mr-2" size={18} />
                  {product.discount} de DESCONTO
                </div>

                <div className="p-6">
                  {/* Icon */}
                  <div className="bg-gradient-to-br from-blue-900 to-blue-800 w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="text-orange-400" size={32} />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {product.name}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-4 text-sm">
                    {product.description}
                  </p>

                  {/* Benefits */}
                  <ul className="space-y-2 mb-6">
                    {product.benefits.slice(0, 3).map((benefit, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-700">
                        <span className="text-orange-500 mr-2 flex-shrink-0">✓</span>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-gray-400 line-through text-lg">
                        R$ {product.originalPrice}
                      </span>
                    </div>
                    <div className="flex items-baseline space-x-1">
                      <span className="text-sm text-gray-600">por apenas</span>
                      <span className="text-3xl font-bold text-blue-900">
                        R$ {product.price}
                      </span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  {product.link ? (
                    <a 
                      href={product.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-lg font-bold text-center hover:from-orange-600 hover:to-orange-700 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                    >
                      Comprar Agora
                      <ExternalLink className="inline-block ml-2" size={18} />
                    </a>
                  ) : (
                    <button 
                      disabled
                      className="block w-full bg-gray-300 text-gray-500 py-3 px-6 rounded-lg font-bold text-center cursor-not-allowed"
                    >
                      Em Breve
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bundle Offer */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 rounded-3xl shadow-2xl overflow-hidden border-4 border-orange-500">
            <div className="p-8 md:p-12 text-white relative">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>

              <div className="relative z-10">
                {/* Badge */}
                <div className="inline-block bg-orange-500 text-white px-6 py-2 rounded-full font-bold text-sm uppercase mb-6 animate-pulse">
                  🔥 Oferta Especial
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex-1">
                    <h3 className="text-3xl md:text-4xl font-bold mb-4">
                      Pacote Completo - Todas as Disciplinas
                    </h3>
                    <p className="text-xl text-gray-200 mb-6">
                      Adquira todos os 8 flashcards com desconto adicional
                    </p>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-orange-400 text-2xl">✓</span>
                        <span className="text-lg">8 disciplinas completas</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-orange-400 text-2xl">✓</span>
                        <span className="text-lg">Revisão ilimitada</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-orange-400 text-2xl">✓</span>
                        <span className="text-lg">Conteúdo sempre atualizado</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20">
                    <div className="text-sm text-gray-300 mb-2">De R$ 79,92 por</div>
                    <div className="text-5xl font-bold text-orange-400 mb-2">
                      R$ 48,99
                    </div>
                    <div className="text-sm text-gray-300 mb-4">
                      Economia de <strong className="text-orange-400">R$ 30,93</strong>
                    </div>
                    <div className="bg-orange-500 text-white px-6 py-2 rounded-full font-bold text-lg mb-4">
                      39% OFF
                    </div>
                    <a 
                      href="https://pay.kiwify.com.br/L5hXJ8s"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-orange-500 text-white py-4 px-8 rounded-xl font-bold text-lg hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
                    >
                      Quero o Pacote Completo
                      <ExternalLink className="inline-block ml-2" size={20} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
